import React, { useState, useEffect } from "react";
import { apiGet } from "../../services/api";
import { useToast } from "../common/ToastProvider";
import Button from "../common/Button";
import "./ConsultantSelectionModal.css";

interface Consultant {
    id: number;
    name: string;
    role?: string;
    is_active?: boolean;
}

interface ConsultantSelectionModalProps {
    isOpen: boolean;
    onSelect: (consultant: Consultant | null) => void; // null means skipped/none
    onClose: () => void; // meaningful mostly if cancelled without selection (maybe treat as skip?)
}

export default function ConsultantSelectionModal({ isOpen, onSelect, onClose }: ConsultantSelectionModalProps) {
    const [consultants, setConsultants] = useState<Consultant[]>([]);
    const [selectedId, setSelectedId] = useState<number | "">("");
    const [loading, setLoading] = useState(false);

    const { showToast } = useToast();

    useEffect(() => {
        if (isOpen && consultants.length === 0) {
            setLoading(true);
            apiGet("/api/admin/consultants")
                .then((data) => {
                    if (Array.isArray(data)) {
                        setConsultants(data.filter((c: Consultant) => c.is_active !== false));
                    }
                })
                .catch((err) => {
                    console.error("Failed to load consultants", err);
                    showToast({ message: "Failed to load consultants.", type: "error" });
                })
                .finally(() => setLoading(false));
        }
    }, [isOpen, consultants.length]);

    if (!isOpen) return null;

    const handleConfirm = () => {
        if (selectedId === "") return;
        const cons = consultants.find(c => c.id === Number(selectedId));
        onSelect(cons || null);
    };

    const handleSkip = () => {
        onSelect(null);
    };

    return (
        <div className="mod-modal-overlay csm-overlay">
            <div className="mod-card csm-card">
                <h3 className="csm-title">Select Consultant</h3>
                <p className="csm-subtitle">Is this procedure being performed by a consultant?</p>

                {loading ? (
                    <div className="csm-loading">Loading consultants...</div>
                ) : (
                    <div className="modal-content csm-content">
                        <select
                            value={selectedId}
                            onChange={(e) => setSelectedId(Number(e.target.value))}
                            className="csm-select"
                        >
                            <option value="" disabled>Select a consultant...</option>
                            {consultants.map(c => (
                                <option key={c.id} value={c.id}>{c.name} ({c.role || "Consultant"})</option>
                            ))}
                        </select>
                    </div>
                )}

                <div className="modal-actions csm-actions">
                    <Button variant="ghost" onClick={handleSkip}>No / Skip</Button>
                    <Button variant="primary" disabled={selectedId === ""} onClick={handleConfirm}>
                        Confirm
                    </Button>
                </div>
            </div>
        </div>
    );
}
